import BigModal from "../components/big-modal";
import Recipe, {
  IngredientUnit,
  RecipeCategory,
  RecipeDifficulty,
  RecipeIngredient,
  RecipeStep,
} from "../models/recipe";
import { ChangeEvent, createRef, useContext, useEffect, useState } from "react";
import FirebaseContext from "../utils/firebase-context";
import Center from "../components/center";
import ImagePicker from "./image-picker";
import Row from "../components/row";
import TextInput from "../components/text-input";
import Select from "../components/select";
import Textarea from "../components/textarea";
import Divider from "../components/divider";
import SpaceBetween from "../components/space-between";
import Heading from "../components/heading";
import AddButton from "./add-button";
import IconButton from "../components/icon-button";
import DeleteLink from "../components/delete-link";
import PrimaryButton from "../components/primary-button";
import { useRecoilValue } from "recoil";
import userState from "../utils/user-state";
import { Subheading } from "./new-recipe-modal";
import DeleteButton from "../components/delete-button";
import { CloseRounded } from "@mui/icons-material";

const emptyIngredient: RecipeIngredient = {
  amount: 0,
  name: "",
  unit: "",
};

const emptyStep: RecipeStep = {
  description: "",
};

export default function EditRecipeModal(props: {
  close: () => void;
  initialRecipe: Recipe;
}) {
  const [recipe, setRecipe] = useState(props.initialRecipe);
  const [initial, setInitial] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [image, setImage] = useState<File>();
  const user = useRecoilValue(userState);

  const firebase = useContext(FirebaseContext);

  const removeIngredient = (index: number) => () => {
    if (recipe.ingredients.length <= 1) return;
    setRecipe((r) => ({
      ...r,
      ingredients: r.ingredients.filter((ing, i) => i !== index),
    }));
  };

  const removeStep = (index: number) => () => {
    if (recipe.steps.length <= 1) return;
    setRecipe((r) => ({ ...r, steps: r.steps.filter((s, i) => i !== index) }));
  };

  const addIngredient = () => {
    setInitial(false);
    setRecipe((r) => ({
      ...r,
      ingredients: [...r.ingredients, emptyIngredient],
    }));
  };

  const addStep = async () => {
    setInitial(false);
    setRecipe((r) => ({ ...r, steps: [...r.steps, emptyStep] }));
  };

  const updateIngredientAmount = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const r = { ...recipe };
    const ingredients = [...r.ingredients];
    ingredients[index] = {
      ...ingredients[index],
      amount: Number(e.target.value),
    };
    r.ingredients = ingredients;

    setRecipe(r);
  };

  const updateIngredientName = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const r = { ...recipe };
    const ingredients = [...r.ingredients];
    ingredients[index] = { ...ingredients[index], name: e.target.value };
    r.ingredients = ingredients;

    setRecipe(r);
  };

  const updateIngredientUnit = (
    e: ChangeEvent<HTMLSelectElement>,
    index: number
  ) => {
    const r = { ...recipe };
    const ingredients = [...r.ingredients];
    ingredients[index] = {
      ...ingredients[index],
      unit: e.target.value as IngredientUnit,
    };
    r.ingredients = ingredients;
    setRecipe(r);
  };

  const updateStep = (e: ChangeEvent<HTMLTextAreaElement>, index: number) => {
    const r = { ...recipe };
    const steps = [...r.steps];
    steps[index] = { description: e.target.value };
    r.steps = steps;
    setRecipe(r);
  };

  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    if (!ref.current || initial) return;
    ref.current.scrollTo({ top: ref.current.scrollHeight });
  }, [ref.current?.scrollTop, ref, initial]);

  const updateImage = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files[0]) return;
    setImage(files[0]);
  };

  const updateRecipe = async () => {
    if (!user) return;
    setDisabled(true);
    const success = await firebase.updateRecipe(recipe, image, user);
    if (success) props.close();
  };

  const deleteRecipe = async () => {
    await firebase.deleteRecipe(recipe);
    props.close();
  };

  return (
    <BigModal title="Rezept bearbeiten" close={props.close} ref={ref}>
      <Center>
        <ImagePicker
          default={recipe.image}
          value={image}
          onChange={updateImage}
          placeholder={"Rezept-Bild"}
        />
      </Center>
      <Row>
        <TextInput
          placeholder="Titel"
          value={recipe.title}
          onChange={(e) => setRecipe({ ...recipe, title: e.target.value })}
        />
        <Row>
          <Select
            value={recipe.category}
            onChange={(e) =>
              setRecipe({
                ...recipe,
                category: e.target.value as RecipeCategory,
              })
            }
          >
            <option value="salate">Salate</option>
            <option value="smoothies">Smoothies & Getränke</option>
            <option value="herzhaftes">Herzhaftes</option>
            <option value="sossen">Soßen & Dips</option>
            <option value="suesses">Süßes</option>
            <option value="lifestyle">Beauty & Lifestyle</option>
          </Select>
          <Select
            value={recipe.difficulty}
            onChange={(e) =>
              setRecipe((r) => ({
                ...r,
                difficulty: e.target.value as RecipeDifficulty,
              }))
            }
          >
            <option value="leicht">Leicht</option>
            <option value="mittel">Mittel</option>
            <option value="schwer">Schwer</option>
          </Select>
        </Row>
      </Row>
      <Row>
        <TextInput
          placeholder={"Zubereitungsdauer (in Minuten)"}
          type={"number"}
          value={recipe.duration === 0 ? "" : recipe.duration}
          onChange={(e) =>
            setRecipe((r) => ({ ...r, duration: Number(e.target.value) }))
          }
        />
        <TextInput
          placeholder={"Personenanzahl"}
          type={"number"}
          value={recipe.people === 0 ? "" : recipe.people}
          onChange={(e) =>
            setRecipe((r) => ({ ...r, people: Number(e.target.value) }))
          }
        />
      </Row>
      <Row>
        <Textarea
          placeholder="Beschreibung"
          rows={6}
          value={recipe.description}
          onChange={(e) =>
            setRecipe((r) => ({ ...r, description: e.target.value }))
          }
        />
      </Row>
      <Divider />

      <Row>
        <div>
          <div style={{ marginBottom: "1rem" }}>
            <SpaceBetween>
              <Heading>Zutaten</Heading>
              <AddButton onClick={addIngredient} />
            </SpaceBetween>
          </div>

          {recipe.ingredients.map((i, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                marginBottom: ".5rem",
              }}
            >
              {recipe.ingredients.length > 1 && (
                <div style={{ marginRight: ".5rem" }}>
                  <IconButton
                    color={"#ff7272"}
                    onClick={removeIngredient(index)}
                  >
                    <CloseRounded />
                  </IconButton>
                </div>
              )}
              <Row style={{ width: "100%" }}>
                <TextInput
                  placeholder={"Menge"}
                  type="number"
                  value={i.amount === 0 ? "" : i.amount}
                  onChange={(e) => updateIngredientAmount(e, index)}
                />

                <Select
                  style={{ width: "100%" }}
                  value={i.unit}
                  onChange={(e) => updateIngredientUnit(e, index)}
                >
                  <option value="">Keine Einheit</option>
                  <option value="EL">EL</option>
                  <option value="TL">TL</option>
                  <option value="Stück">Stück</option>
                  <option value="g">Gramm</option>
                  <option value="kg">KG</option>
                  <option value="Prise">Prise</option>
                  <option value="ml">ml</option>
                  <option value="cl">cl</option>
                  <option value="l">Liter</option>
                  <option value="Hand voll">Handvoll</option>
                  <option value="Bund">Bund</option>
                  <option value="Zweige">Zweige</option>
                </Select>

                <TextInput
                  placeholder={"Name"}
                  value={i.name}
                  onChange={(e) => updateIngredientName(e, index)}
                />
              </Row>
            </div>
          ))}
        </div>
      </Row>
      <Divider />
      <Row>
        <div>
          <div style={{ marginBottom: "1rem" }}>
            <SpaceBetween>
              <Heading>Zubereitung</Heading>
              <AddButton onClick={addStep} />
            </SpaceBetween>
          </div>

          {recipe.steps.map((step, index) => (
            <div key={index}>
              <SpaceBetween>
                <Subheading>Schritt {index + 1}</Subheading>
                {recipe.steps.length > 1 && (
                  <DeleteLink onClick={removeStep(index)}>Löschen</DeleteLink>
                )}
              </SpaceBetween>
              <Textarea
                placeholder={"Beschreibung"}
                rows={2}
                value={step.description}
                onChange={(e) => updateStep(e, index)}
              />
            </div>
          ))}
        </div>
      </Row>
      <PrimaryButton onClick={updateRecipe} disabled={disabled}>
        Änderungen übernehmen
      </PrimaryButton>
      <DeleteButton onClick={deleteRecipe} disabled={disabled}>
        Rezept löschen
      </DeleteButton>
    </BigModal>
  );
}
